import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import useSEO from "@/hooks/useSEO";
import Reveal, { Stagger } from "@/components/Reveal";
import AnimatedNumber from "@/components/AnimatedNumber";
import {
  ArrowUpRight, ArrowRight, TrendingUp, Target, BarChart3, Sparkles,
  CheckCircle2, Megaphone, Search, Share2, Layers, Wand2, Workflow,
} from "lucide-react";

const DIFFERENTIATORS = [
  { icon: Target, label: "Performance-first approach", color: "blue" },
  { icon: Layers, label: "Full-funnel marketing expertise", color: "red" },
  { icon: Wand2, label: "Creative + Data combination", color: "green" },
  { icon: BarChart3, label: "Transparent reporting", color: "blue" },
  { icon: Sparkles, label: "Dedicated account management", color: "red" },
  { icon: TrendingUp, label: "Affordable growth solutions", color: "green" },
  { icon: Workflow, label: "Marketing automation expertise", color: "blue" },
  { icon: CheckCircle2, label: "Proven lead generation frameworks", color: "red" },
];

const SERVICE_PREVIEW = [
  { icon: Megaphone, title: "Performance Marketing", desc: "High-ROI campaigns across Google, Meta, LinkedIn & YouTube.", accent: "border-t-saavi-blue" },
  { icon: Search, title: "SEO & AI Search", desc: "Rank where customers search — technical, on-page, local & AI.", accent: "border-t-saavi-green" },
  { icon: Share2, title: "Social & Content", desc: "Build community, brand voice, and content that converts.", accent: "border-t-saavi-red" },
  { icon: Workflow, title: "Automation & Funnels", desc: "Email, WhatsApp, CRM & lead-nurturing funnels.", accent: "border-t-saavi-blue" },
];

const STATS = [
  { k: "300K+", v: "Qualified leads generated", c: "text-saavi-blue" },
  { k: "130%", v: "Avg. lead target growth", c: "text-saavi-red" },
  { k: "35%", v: "Lower cost per lead", c: "text-saavi-green" },
  { k: "100K+", v: "Community followers built", c: "text-saavi-blue" },
];

export default function Home() {
  useSEO();
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" aria-hidden="true" />
        <div aria-hidden="true" className="blob bg-saavi-blue/30 w-[420px] h-[420px] top-[-120px] right-[-80px]" />
        <div aria-hidden="true" className="blob bg-saavi-red/20 w-[320px] h-[320px] bottom-[-100px] left-[-60px]" style={{ animationDelay: "-6s" }} />

        <div className="container-saavi relative grid grid-cols-1 lg:grid-cols-12 gap-12 py-16 md:py-24 lg:py-32">
          <div className="lg:col-span-7">
            <Reveal variant="fade-up" delay={0}>
              <div className="font-mono-overline text-muted-foreground mb-6 flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-saavi-blue pulse-dot" />
                Growth Marketing · Performance · Brand
              </div>
            </Reveal>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl">
              <Reveal as="span" variant="blur-in" delay={80} className="block">Grow Faster.</Reveal>
              <Reveal as="span" variant="blur-in" delay={220} className="block text-saavi-blue">Scale Smarter.</Reveal>
              <Reveal as="span" variant="blur-in" delay={360} className="block">
                Convert <span className="relative inline-block">
                  Better.
                  <span className="absolute -bottom-1 left-0 right-0 h-1 underline-shimmer" />
                </span>
              </Reveal>
            </h1>
            <Reveal variant="fade-up" delay={520}>
              <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
                We help businesses generate high-quality leads, increase revenue, and build powerful digital brands through data-driven marketing strategies.
                From Performance Marketing and Social Media to SEO, Content, Automation, and Lead Generation — Saavi Digitex becomes your growth partner, not just another agency.
              </p>
            </Reveal>
            <Reveal variant="fade-up" delay={640}>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/contact">
                  <Button data-testid="hero-primary-cta" size="lg" className="h-12 rounded-md gap-2 px-6 text-base group">
                    Book a Free Strategy Call <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button data-testid="hero-secondary-cta" size="lg" variant="outline" className="h-12 rounded-md px-6 text-base">
                    Get a Marketing Audit
                  </Button>
                </Link>
              </div>
            </Reveal>
            <Reveal variant="fade-in" delay={780}>
              <div className="mt-10 flex items-center gap-3 text-sm text-muted-foreground">
                <span className="inline-flex h-2 w-2 rounded-full bg-saavi-green pulse-dot" />
                Trusted by Startups, SMEs, Educational Institutions & Enterprises
              </div>
            </Reveal>
          </div>

          <Reveal variant="scale-in" delay={300} className="lg:col-span-5">
            <div className="relative float-y">
              <div className="absolute -inset-4 bg-saavi-blue/10 rounded-md blur-2xl pointer-events-none" />
              <div className="relative border border-border rounded-md bg-card overflow-hidden">
                <div className="border-b border-border px-5 py-3 flex items-center justify-between">
                  <span className="font-mono-overline text-muted-foreground">Live · Growth Dashboard</span>
                  <span className="inline-flex h-2 w-2 rounded-full bg-saavi-green pulse-dot" />
                </div>
                <div className="p-6 space-y-5">
                  {[
                    { l: "Leads Generated", v: "12,438", d: "+34%", c: "text-saavi-blue" },
                    { l: "Cost Per Lead", v: "₹186", d: "−35%", c: "text-saavi-green" },
                    { l: "ROAS", v: "6.2x", d: "+42%", c: "text-saavi-red" },
                  ].map((r, i) => (
                    <Reveal key={r.l} variant="slide-right" delay={500 + i * 120}>
                      <div className="flex items-end justify-between border-b border-border pb-4 last:border-0 last:pb-0">
                        <div>
                          <div className="font-mono-overline text-muted-foreground">{r.l}</div>
                          <div className="text-3xl font-extrabold tracking-tight mt-1">
                            <AnimatedNumber value={r.v} />
                          </div>
                        </div>
                        <div className={`text-sm font-bold ${r.c}`}>
                          <AnimatedNumber value={r.d} />
                        </div>
                      </div>
                    </Reveal>
                  ))}
                  <Reveal variant="fade-up" delay={900}>
                    <img
                      src="https://images.pexels.com/photos/97080/pexels-photo-97080.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600"
                      alt="growth analytics"
                      className="w-full h-40 object-cover rounded-md border border-border mt-2"
                    />
                  </Reveal>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE — trusted-by strip */}
      <section className="border-b border-border bg-secondary/40 py-5">
        <div className="marquee">
          {[0, 1].map((dup) => (
            <div key={dup} className="marquee__track" aria-hidden={dup === 1}>
              {[
                "Higher Education",
                "Healthcare",
                "Real Estate",
                "E-Commerce",
                "Hospitality",
                "Professional Services",
                "EdTech",
                "D2C Brands",
                "Hotels & Resorts",
                "Financial Services",
              ].map((label, i) => (
                <span key={`${dup}-${i}`} className="font-mono-overline text-muted-foreground inline-flex items-center gap-3 whitespace-nowrap">
                  <span className="h-1.5 w-1.5 rounded-full bg-saavi-blue" />
                  {label}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="border-b border-border">
        <div className="container-saavi grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-border">
          {STATS.map((s, i) => (
            <Reveal key={s.v} variant="fade-up" delay={i * 110}>
              <div className="p-8 md:p-10">
                <div className={`text-4xl md:text-5xl font-extrabold tracking-tight ${s.c}`}>
                  <AnimatedNumber value={s.k} duration={1600 + i * 200} />
                </div>
                <div className="mt-2 text-sm text-muted-foreground">{s.v}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="section-pad border-b border-border">
        <div className="container-saavi">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
            <Reveal variant="fade-up" className="lg:col-span-5">
              <div className="font-mono-overline text-saavi-blue mb-4">Why Saavi Digitex</div>
              <h2 className="text-4xl md:text-5xl">Results Driven.<br /><span className="text-saavi-red">ROI Focused.</span></h2>
            </Reveal>
            <Reveal variant="fade-up" delay={120} className="lg:col-span-7 flex items-end">
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                We don't believe in vanity metrics. Every campaign, click, and creative is designed to drive measurable business outcomes — qualified pipeline, lower CAC, and durable revenue growth.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {DIFFERENTIATORS.map(({ icon: Icon, label, color }, i) => (
              <Reveal key={label} variant="fade-up" delay={i * 70}>
                <div
                  data-testid={`differentiator-${i}`}
                  className="card-hover lift-on-hover ai-card border border-border rounded-md p-6 bg-card h-full"
                >
                  <div className={`inline-flex h-10 w-10 items-center justify-center rounded-md border border-border text-saavi-${color} transition-transform duration-300 hover:scale-110 hover:rotate-3`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="mt-5 text-base font-semibold leading-snug">{label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="section-pad border-b border-border">
        <div className="container-saavi">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <Reveal variant="fade-up">
              <div className="font-mono-overline text-saavi-green mb-3">What We Do</div>
              <h2 className="text-4xl md:text-5xl max-w-2xl">A complete growth stack under one roof.</h2>
            </Reveal>
            <Reveal variant="fade-up" delay={120}>
              <Link to="/services" className="inline-flex items-center gap-1 text-sm font-semibold hover:text-saavi-blue group">
                See all services
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {SERVICE_PREVIEW.map(({ icon: Icon, title, desc, accent }, i) => (
              <Reveal key={title} variant="fade-up" delay={i * 90}>
                <Link
                  to="/services"
                  data-testid={`service-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`card-hover lift-on-hover ai-card group border border-border border-t-2 ${accent} rounded-md p-6 bg-card block h-full`}
                >
                  <Icon className="h-6 w-6 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-0.5" />
                  <h3 className="mt-5 text-xl">{title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{desc}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold group-hover:text-saavi-blue">
                    Learn more <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="section-pad">
        <div className="container-saavi">
          <Reveal variant="scale-in">
            <div className="relative overflow-hidden border border-border rounded-md bg-card p-10 md:p-16">
              <div className="blob bg-saavi-blue/30 w-64 h-64 top-[-100px] right-[-100px]" aria-hidden="true" />
              <div className="blob bg-saavi-red/20 w-64 h-64 bottom-[-100px] left-[-100px]" style={{ animationDelay: "-8s" }} aria-hidden="true" />
              <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="font-mono-overline text-saavi-blue mb-4">Ready when you are</div>
                  <h2 className="text-3xl md:text-4xl">Let's build your growth engine.</h2>
                  <p className="mt-4 text-muted-foreground max-w-lg">
                    Book a free 30-min strategy call. We'll audit your current funnel and share 3 actionable opportunities — no obligations.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <Link to="/contact"><Button size="lg" className="h-12 px-6 gap-2 group">Book Free Call <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></Button></Link>
                  <Link to="/packages"><Button size="lg" variant="outline" className="h-12 px-6">View Packages</Button></Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
